import { PlatformType } from '../types/common';
import { CAROUSEL_TYPES, ProductCardVariant } from './constants';

type DeviceType = 'mobile' | 'tablet' | 'laptop' | 'desktop' | 'desktopLarge';

type ProductItemsCount = Record<DeviceType, Record<ProductCardVariant, number>>;

const getDefaultProductItemsCount = (): ProductItemsCount => {
  return {
    mobile: {
      [ProductCardVariant.NORMAL]: 2.1,
      [ProductCardVariant.COMPACT]: 2.9,
      [ProductCardVariant.COMPACT_MINI]: 4,
    },
    tablet: {
      [ProductCardVariant.NORMAL]: 3.5,
      [ProductCardVariant.COMPACT]: 5.5,
      [ProductCardVariant.COMPACT_MINI]: 7.5,
    },
    laptop: {
      [ProductCardVariant.NORMAL]: 5.5,
      [ProductCardVariant.COMPACT]: 7.5,
      [ProductCardVariant.COMPACT_MINI]: 9.5,
    },
    desktop: {
      [ProductCardVariant.NORMAL]: 5.5,
      [ProductCardVariant.COMPACT]: 7.5,
      [ProductCardVariant.COMPACT_MINI]: 9.5,
    },
    desktopLarge: {
      [ProductCardVariant.NORMAL]: 6.5,
      [ProductCardVariant.COMPACT]: 8.5,
      [ProductCardVariant.COMPACT_MINI]: 10.5,
    },
  };
};

export interface CarouselStyleConfig {
  slidesPerView: number;
  showArrow: boolean;
  showSeeAllButton: boolean;
  showSeeAllCard: boolean;
  gap: number;
  fontStyle: {
    fontTitleSize: number;
    fontTitleWeight: number;
    fontContentSize: number;
    fontContentWeight: number;
    fontTitleSizeRevamp?: number;
    fontTitleWeightRevamp?: number;
    fontFamilyRevamp?: string;
    fontStyleRevamp?: string;
    lineHeightRevamp?: string;
    fontColorRevamp?: string;
    titleDisplayRevamp?: string;
    titleAlignItemsRevamp?: string;
    titleHeightRevamp?: string;
  };
  responsive?: {
    mobile: {
      slidesPerView: number;
      showArrow: boolean;
      gap: number;
    };
    tablet: {
      slidesPerView: number;
      showArrow: boolean;
      gap: number;
    };
    laptop: {
      slidesPerView: number;
      showArrow: boolean;
      gap: number;
    };
    desktop: {
      slidesPerView: number;
      showArrow: boolean;
      gap: number;
    };
    desktopLarge: {
      slidesPerView: number;
      showArrow: boolean;
      gap: number;
    };
  };
}

export const getCarouselStyleConfig = (
  carouselType: string,
  platform: PlatformType | null,
  productCardVariant: ProductCardVariant,
): CarouselStyleConfig => {
  const defaultProductItemsCount = getDefaultProductItemsCount();
  const defaultConfig: CarouselStyleConfig = {
    slidesPerView: defaultProductItemsCount.mobile[productCardVariant],
    showArrow: platform !== 'app',
    showSeeAllButton: false,
    showSeeAllCard: false,
    gap: 0,
    fontStyle: {
      fontTitleSize: platform === 'app' ? 16 : 18,
      fontTitleWeight: platform === 'app' ? 700 : 600,
      fontContentSize: platform === 'app' ? 12 : 16,
      fontContentWeight: platform === 'app' ? 550 : 500,
    },
    responsive: {
      mobile: {
        slidesPerView: defaultProductItemsCount.mobile[productCardVariant],
        showArrow: false,
        gap: 1,
      },
      tablet: {
        slidesPerView: defaultProductItemsCount.tablet[productCardVariant],
        showArrow: false,
        gap: 1,
      },
      laptop: {
        slidesPerView: defaultProductItemsCount.laptop[productCardVariant],
        showArrow: false,
        gap: 1,
      },
      desktop: {
        slidesPerView: defaultProductItemsCount.desktop[productCardVariant],
        showArrow: false,
        gap: 1,
      },
      desktopLarge: {
        slidesPerView: defaultProductItemsCount.desktopLarge[productCardVariant],
        showArrow: false,
        gap: 1,
      },
    },
  };

  switch (carouselType) {
    case CAROUSEL_TYPES.TOP_PICKS:
    case CAROUSEL_TYPES.AGEING_RECOMMENDATIONS:
    case CAROUSEL_TYPES.NEW_ARRIVALS_RECOMMENDATIONS:
    case CAROUSEL_TYPES.HOME_RECOMMENDATIONS:
      return {
        ...defaultConfig,
        showSeeAllCard: true,
        showSeeAllButton: true,
        showArrow: platform !== 'app',
      };

    case CAROUSEL_TYPES.SEARCH_START_TOP_PICKS:
      return {
        ...defaultConfig,
        fontStyle: {
          ...defaultConfig.fontStyle,
          fontTitleSize: 16,
          fontTitleWeight: platform === 'app' ? 700 : 600,
        },
        showSeeAllButton: true,
        showSeeAllCard: true,
        showArrow: platform === 'app' ? false : true,
      };

    case CAROUSEL_TYPES.BRANDS:
      return {
        ...defaultConfig,
        showSeeAllCard: platform === 'app',
        showSeeAllButton: true,
        fontStyle: {
          fontTitleSize: platform === 'app' ? 16 : 18,
          fontTitleWeight: platform === 'app' ? 700 : 600,
          fontContentSize: platform === 'app' ? 14 : 16,
          fontContentWeight: 400,
        },
        responsive: {
          ...defaultConfig.responsive!,
          mobile: {
            ...defaultConfig.responsive!.mobile,
            slidesPerView: platform !== 'app' ? 4 : 2.8,
          },
        },
      };

    case CAROUSEL_TYPES.SIMILAR_PRODUCTS:
    case CAROUSEL_TYPES.OTHER_SIMILAR_PRODUCT:
    case CAROUSEL_TYPES.CATEGORIES:
    case CAROUSEL_TYPES.BOUGHTTOGETHER:
    case CAROUSEL_TYPES.BASKET_RECOMMENDATION:
      return {
        ...defaultConfig,
        showSeeAllCard: true,
        showSeeAllButton: true,
        fontStyle: {
          fontTitleSize: platform === 'app' ? 16 : 18,
          fontTitleWeight: platform === 'app' ? 700 : 600,
          fontContentSize: platform === 'app' ? 14 : 16,
          fontContentWeight: 400,
          fontTitleSizeRevamp: 20,
          fontTitleWeightRevamp: 700,
          fontFamilyRevamp: '"Google Sans", sans-serif',
          fontStyleRevamp: 'normal',
          lineHeightRevamp: '32px',
          fontColorRevamp: '#212124',
          titleDisplayRevamp: 'flex',
          titleAlignItemsRevamp: 'center',
          titleHeightRevamp: '32px',
        },
      };

    case CAROUSEL_TYPES.FRESH_FOOD_CAROUSEL:
      return {
        ...defaultConfig,
        showSeeAllCard: true,
        showSeeAllButton: true,
      };

    case CAROUSEL_TYPES.ADS_CAROUSEL:
      return {
        ...defaultConfig,
        showSeeAllCard: false,
        showSeeAllButton: false,
        showArrow: platform === 'app' ? false : true,
        fontStyle: {
          ...defaultConfig.fontStyle,
          fontTitleSize: platform === 'app' ? 12 : 16,
          fontTitleWeight: platform === 'app' ? 700 : 600,
        },
        responsive: {
          ...defaultConfig.responsive!,
          mobile: {
            ...defaultConfig.responsive!.mobile,
            slidesPerView: 4,
            showArrow: false,
            gap: 1,
          },
          tablet: {
            ...defaultConfig.responsive!.tablet,
            slidesPerView: 7,
            showArrow: false,
            gap: 1,
          },
          laptop: {
            ...defaultConfig.responsive!.laptop,
            slidesPerView: 7,
            showArrow: false,
            gap: 1,
          },
          desktop: {
            ...defaultConfig.responsive!.desktop,
            slidesPerView: 8,
            showArrow: false,
            gap: 1,
          },
          desktopLarge: {
            ...defaultConfig.responsive!.desktopLarge,
            slidesPerView: 10,
            showArrow: false,
            gap: 1,
          },
        },
      };

    default:
      return defaultConfig;
  }
};
